import { message } from 'antd';
import React, { useEffect, useRef, useState } from 'react';

import AppleIcon from '@/components/icons/AppleIcon';
import { useTranslate } from '@/hooks/useTranslate';
import { checkLogin, loginOrInsert } from '@/services/login';
import useLoginStore from '@/store/Login';
import {
  APPLE_AUTH_KEYS,
  createAppleAuthToken,
  setAppleAuthValue,
} from '@/utils/appleAuth';
import { setCookie } from '@/utils/cookie';
import { isProduction } from '@/utils/envHelper';
import { EventNewID, GoogleEventID } from '@/utils/eventlog/eventNewConfig';
import { trackEvent, trackGoogleEvent } from '@/utils/eventlog/logAdapter';
import { getFingerprint } from '@/utils/fingerprint';

import styles from './style.module.scss';

interface AppleLoginProps {
  disabled?: boolean;
  onSuccess: () => Promise<void>;
  onError?: (error: any) => void;
}

const APPLE_SCRIPT_SRC =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

const loadAppleScript = () =>
  new Promise<void>((resolve, reject) => {
    if (typeof window === 'undefined') return resolve();
    if ((window as any).AppleID) return resolve();
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${APPLE_SCRIPT_SRC}"]`,
    );
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('apple_script_error')), {
        once: true,
      });
      return;
    }
    const script = document.createElement('script');
    script.src = APPLE_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('apple_script_error'));
    document.head.appendChild(script);
  });

const decodeJwtPayload = (token: string) => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
};

export const AppleLogin: React.FC<AppleLoginProps> = ({
  disabled,
  onSuccess,
  onError,
}) => {
  const { t } = useTranslate();
  const clientId = process.env.NEXT_PUBLIC_APPLE_CLIENT_ID || '';

  const redirectUri = isProduction
    ? 'https://falcocut.ai/api/auth/apple/callback'
    : 'https://falcocut-qa.fireflyfusion.cn/api/auth/apple/callback';

  const [loading, setLoading] = useState(false);
  const handledRef = useRef(false);

  useEffect(() => {
    const onSuccessEvent = (event: any) => {
      const detail = event?.detail?.data || event?.detail || {};
      handleAppleResponse(detail);
    };
    const onFailureEvent = (event: any) => {
      const error = event?.detail?.error || event?.detail || event;
      handleAppleError(error);
    };
    document.addEventListener('AppleIDSignInOnSuccess', onSuccessEvent as EventListener);
    document.addEventListener('AppleIDSignInOnFailure', onFailureEvent as EventListener);
    return () => {
      document.removeEventListener(
        'AppleIDSignInOnSuccess',
        onSuccessEvent as EventListener,
      );
      document.removeEventListener(
        'AppleIDSignInOnFailure',
        onFailureEvent as EventListener,
      );
    };
  }, []);

  const handleAppleError = (error: any) => {
    if (handledRef.current) return;
    handledRef.current = true;
    setLoading(false);
    message.error(t('merror.login.apple.failed'));
    trackEvent(EventNewID.APPLE_LOGIN_FAILED, { loginResult: error || 'unknown' });
    onError?.(error);
    setTimeout(() => {
      handledRef.current = false;
    }, 500);
  };

  const handleAppleResponse = async (detail: any) => {
    if (handledRef.current) return;
    handledRef.current = true;
    setLoading(true);
    try {
      const code = detail?.authorization?.code || detail?.code;
      const idToken = detail?.authorization?.id_token || detail?.id_token;
      const state = detail?.authorization?.state || detail?.state;
      const storedState =
        typeof window !== 'undefined'
          ? window.sessionStorage.getItem(APPLE_AUTH_KEYS.state)
          : '';
      if (!code) {
        throw new Error('missing_code');
      }
      if (!idToken) {
        throw new Error('missing_id_token');
      }
      const payload = decodeJwtPayload(idToken);
      const sub = payload?.sub;
      if (!sub) {
        throw new Error('missing_sub');
      }
      if (storedState && state && storedState !== state) {
        throw new Error('state_mismatch');
      }
      const checkResult = await checkLogin({
        accountType: 53,
        accountId: sub,
      });
      if (checkResult?.data?.state === 1) {
        useLoginStore.getState().setIsNewUser(true);
      }
      const fingerprint = await getFingerprint();
      const loginRes = await loginOrInsert({
        accountType: 53,
        accountId: sub,
        authCode: code,
        fingerprint,
      });

      if (loginRes?.code !== 200) {
        throw new Error(loginRes?.message || 'login_failed');
      }
      const res = await checkLogin({
        accountType: 65,
        accountId: code,
      });

      if (res?.data?.state === 1) {
        trackGoogleEvent(GoogleEventID.SignUp, {
          user_id: code,
          is_free_user: true,
        });
      }

      setCookie(
        'user',
        {
          ...loginRes.data,
          accountId: sub,
          email: payload?.email,
          accountType: 53,
        },
        30,
      );
      if (loginRes.data?.uid) {
        setCookie('falcocut_user_id', loginRes.data.uid, 30);
      }
      setLoading(false);
      await onSuccess();
    } catch (error: any) {
      setLoading(false);
      trackEvent(EventNewID.APPLE_LOGIN_FAILED, { loginResult: error?.message || error });
      message.error(t('merror.login.apple.failed'));
      onError?.(error);
    } finally {
      setTimeout(() => {
        handledRef.current = false;
      }, 500);
    }
  };

  const handleLoginClick = () => {
    if (disabled || loading) return;
    if (!clientId || !redirectUri) {
      message.error(t('merror.login.apple.not.configured'));
      trackEvent(EventNewID.APPLE_LOGIN_FAILED, { loginResult: 'missing_config' });
      return;
    }

    setLoading(true);
    const state = createAppleAuthToken();
    const nonce = createAppleAuthToken();
    setAppleAuthValue(APPLE_AUTH_KEYS.state, state);
    setAppleAuthValue(APPLE_AUTH_KEYS.nonce, nonce);
    loadAppleScript()
      .then(() => {
        const apple = (window as any).AppleID;
        if (!apple?.auth) {
          throw new Error('apple_sdk_not_ready');
        }
        apple.auth.init({
          clientId,
          scope: 'name email',
          redirectURI: redirectUri,
          state,
          nonce,
          usePopup: true,
          responseType: 'code id_token',
        });
        return apple.auth.signIn();
      })
      .then((response: any) => {
        if (response) {
          handleAppleResponse(response);
        }
      })
      .catch((error: any) => {
        trackEvent(EventNewID.APPLE_LOGIN_FAILED, {
          loginResult: error?.message || error || 'popup_failed',
        });
        handleAppleError(error);
      });
  };

  return (
    <button
      className={styles.appleButton}
      onClick={handleLoginClick}
      disabled={disabled || loading}
      type="button"
      element-name="apple_btn_login_popup"
    >
      <span className={styles.appleIcon}>
        <AppleIcon />
      </span>
      <span className={styles.appleText}>{t('merror.login.apple')}</span>
    </button>
  );
};
